import React, {useMemo, useState} from 'react';
import {Head, Link} from 'zudoku/components';
import {
  ArrowRight,
  Bot,
  Box,
  Check,
  Code2,
  Copy,
  GitBranch,
  Globe2,
  KeyRound,
  LifeBuoy,
  Radio,
  Rocket,
  ShieldCheck,
  TerminalSquare,
  Zap,
} from 'lucide-react';
import {getProducts, type Locale} from '../data/products';
import styles from './home.module.css';

type CopyBlock = {
  title: string;
  description: string;
  primary: string;
  secondary: string;
  connectorLink: string;
  pathEyebrow: string;
  pathTitle: string;
  pathDescription: string;
  docsEyebrow: string;
  docsTitle: string;
  docsDescription: string;
  communityEyebrow: string;
  communityTitle: string;
  communityDescription: string;
};

type LinkItem = {label: string; href: string};
type Card = {title: string; description: string; links: LinkItem[]; icon: React.ComponentType<{size?: number}>};
type TextCard = {title: string; description: string; href: string; icon: React.ComponentType<{size?: number}>};
type CodeExample = {label: string; iconSrc: string; code: string};

const copy: Record<Locale, CopyBlock> = {
  'zh-CN': {
    title: '使用',
    description: '接入市场数据、交易、账户和实时接口，构建机器人、后端服务、仪表盘以及机构级 WEEX 集成方案。',
    primary: '快速开始',
    secondary: '探索 API',
    connectorLink: '查看 WEEX API 参考',
    pathEyebrow: '选择你的路径',
    pathTitle: '从合适的位置开始',
    pathDescription: '无论你是首次接入 WEEX API、正在了解产品系列，还是在为 AI Agent 准备上下文，都可以从最适合你工作流的入口开始。',
    docsEyebrow: '关键文档',
    docsTitle: '生产环境必读',
    docsDescription: '在正式上线集成前，建议先阅读这些跨产品的重要文档。',
    communityEyebrow: '社区',
    communityTitle: '保持联系',
    communityDescription: '关注更新、反馈问题，并与其他在 WEEX 上构建应用的开发者交流。',
  },
  en: {
    title: 'Build with',
    description: 'Connect to market data, trading, accounts, and real-time interfaces to build bots, backends, dashboards, and institutional WEEX integrations.',
    primary: 'Quick start',
    secondary: 'Explore API',
    connectorLink: 'View WEEX API reference',
    pathEyebrow: 'Choose your path',
    pathTitle: 'Start in the right place',
    pathDescription: 'Whether you are new to WEEX APIs, exploring product families, or preparing context for AI agents, start from the entry point that fits your workflow.',
    docsEyebrow: 'Featured docs',
    docsTitle: 'Production essentials',
    docsDescription: 'Read these cross-product documents before shipping an integration to production.',
    communityEyebrow: 'Community',
    communityTitle: 'Stay connected',
    communityDescription: 'Follow updates, share feedback, and talk with other developers building on WEEX.',
  },
};

function codeExamples(locale: Locale): CodeExample[] {
  const zh = locale === 'zh-CN';

  return [
    {
      label: 'Python',
      iconSrc: 'https://cdn.simpleicons.org/python/3776AB',
      code: `import requests

# ${zh ? '公共接口 —— 无需 API Key' : 'Public endpoint - no API key required'}
resp = requests.get(
    "https://api-contract.weex.com/capi/v3/market/ticker/24hr",
    params={"symbol": "BTCUSDT"},
)
print(resp.json())
# -> {"symbol": "BTCUSDT", "lastPrice": "84523.00"}`,
    },
    {
      label: 'JavaScript',
      iconSrc: 'https://cdn.simpleicons.org/javascript/F7DF1E',
      code: `const params = new URLSearchParams({ symbol: "BTCUSDT" });

// ${zh ? '公共接口 —— 无需 API Key' : 'Public endpoint - no API key required'}
const resp = await fetch(
  \`https://api-contract.weex.com/capi/v3/market/ticker/24hr?\${params}\`
);
console.log(await resp.json());`,
    },
    {
      label: 'Java',
      iconSrc: 'https://cdn.simpleicons.org/openjdk/EA2D2E',
      code: `HttpClient client = HttpClient.newHttpClient();
HttpRequest request = HttpRequest.newBuilder()
    .uri(URI.create("https://api-contract.weex.com/capi/v3/market/ticker/24hr?symbol=BTCUSDT"))
    .GET()
    .build();

System.out.println(client.send(request, BodyHandlers.ofString()).body());`,
    },
    {
      label: 'Rust',
      iconSrc: 'https://cdn.simpleicons.org/rust/000000/ffffff',
      code: `let url = "https://api-contract.weex.com/capi/v3/market/ticker/24hr?symbol=BTCUSDT";
let body = reqwest::get(url).await?.text().await?;

println!("{}", body);`,
    },
    {
      label: 'Go',
      iconSrc: 'https://cdn.simpleicons.org/go/00ADD8',
      code: `resp, err := http.Get("https://api-contract.weex.com/capi/v3/market/ticker/24hr?symbol=BTCUSDT")
if err != nil {
    panic(err)
}
defer resp.Body.Close()

body, _ := io.ReadAll(resp.Body)
fmt.Println(string(body))`,
    },
  ];
}

function pathCards(locale: Locale): Card[] {
  const zh = locale === 'zh-CN';
  const prefix = zh ? '/zh-CN' : '';
  const products = getProducts(locale);
  const productById = Object.fromEntries(products.map((product) => [product.id, product]));

  return [
    {
      title: zh ? '快速开始' : 'Quick start',
      description: zh ? '适合首次接入并理解平台能力。' : 'Best for the first integration and understanding platform capabilities.',
      icon: Rocket,
      links: [
        {label: zh ? '介绍' : 'Introduction', href: `${prefix}/introduction`},
        {label: zh ? '集成准备' : 'Integration preparation', href: `${prefix}/products/spot/QuickStart/IntegrationPreparation`},
        {label: zh ? 'API 域名' : 'API domain', href: `${prefix}/products/spot/QuickStart/APIDomain`},
      ],
    },
    {
      title: zh ? '探索产品' : 'Explore products',
      description: zh ? '直接进入最常用的 WEEX 产品系列。' : 'Jump into the most commonly used WEEX product families.',
      icon: Box,
      links: [
        {label: productById.spot?.name ?? 'Spot', href: productById.spot?.href ?? `${prefix}/catalog`},
        {label: productById.futures?.name ?? 'Futures', href: productById.futures?.href ?? `${prefix}/catalog`},
        {label: productById.broker?.name ?? 'Broker', href: productById.broker?.href ?? `${prefix}/catalog`},
      ],
    },
    {
      title: zh ? '使用 API 构建' : 'Build with APIs',
      description: zh ? '查看 REST API、WebSocket 数据流和机构接口。' : 'Use REST APIs, WebSocket streams, and institutional endpoints.',
      icon: TerminalSquare,
      links: [
        {label: 'REST API', href: `${prefix}/catalog/core-trading-spot/api/rest-api/configapi`},
        {label: zh ? 'WebSocket 数据流' : 'WebSocket Streams', href: `${prefix}/catalog/core-trading-spot/api/ws-streams/public`},
        {label: productById.partner?.name ?? (zh ? '合伙人' : 'Partner'), href: productById.partner?.href ?? `${prefix}/catalog`},
      ],
    },
    {
      title: zh ? 'Agent 原生' : 'Agent Native',
      description: zh ? '通过机器可读文档与 AI Agent 集成。' : 'Integrate AI agents with machine-readable documentation.',
      icon: Bot,
      links: [
        {label: zh ? '概览' : 'Overview', href: `${prefix}/agent-native/overview`},
        {label: 'llms.txt', href: `${prefix}/llms.txt`},
        {label: 'llms-full.txt', href: `${prefix}/llms-full.txt`},
      ],
    },
  ];
}

function featuredDocs(locale: Locale): TextCard[] {
  const zh = locale === 'zh-CN';
  const prefix = zh ? '/zh-CN' : '';

  return [
    {
      title: zh ? '认证' : 'Authentication',
      description: zh ? 'API Key、签名和已认证请求。' : 'API keys, signatures, and authenticated requests.',
      href: `${prefix}/products/spot/QuickStart/Signature`,
      icon: KeyRound,
    },
    {
      title: zh ? '访问限制' : 'Access restrictions',
      description: zh ? '权限、IP 白名单和安全访问规则。' : 'Permissions, IP allowlists, and secure access rules.',
      href: `${prefix}/products/spot/QuickStart/AccessRestrictions`,
      icon: ShieldCheck,
    },
    {
      title: zh ? '环境' : 'Environments',
      description: zh ? '各产品的生产环境域名与接入说明。' : 'Production domains and connection details for each product.',
      href: `${prefix}/products/spot/QuickStart/APIDomain`,
      icon: Globe2,
    },
    {
      title: zh ? '支持' : 'Support',
      description: zh ? '获取帮助并关注官方更新渠道。' : 'Get help and follow official update channels.',
      href: `${prefix}/introduction`,
      icon: LifeBuoy,
    },
  ];
}

function communityLinks(locale: Locale): TextCard[] {
  const zh = locale === 'zh-CN';

  return [
    {
      title: 'X',
      description: zh ? '关注 WEEX 中文官方动态。' : 'Follow WEEX official updates.',
      href: zh ? 'https://x.com/WeexCn' : 'https://x.com/WEEX_Official',
      icon: Zap,
    },
    {
      title: 'Telegram',
      description: zh ? '加入 WEEX 中文 Telegram 社群。' : 'Join the WEEX English Telegram community.',
      href: zh ? 'https://t.me/+7jac6zttXxZjOTRl' : 'https://t.me/+Y72JdNeHcUw3NWQ1',
      icon: Radio,
    },
    {
      title: 'Discord',
      description: zh ? '与全球开发者和用户交流。' : 'Talk with global developers and WEEX users.',
      href: 'https://discord.com/invite/2CFnGbMzbh',
      icon: Code2,
    },
    {
      title: 'GitHub',
      description: zh ? '查看 WEEX 开源项目。' : 'Explore WEEX open-source projects.',
      href: 'https://github.com/weex-labs',
      icon: GitBranch,
    },
  ];
}

export default function Home({locale}: {locale: Locale}): React.ReactNode {
  const text = copy[locale];
  const zh = locale === 'zh-CN';
  const prefix = zh ? '/zh-CN' : '';
  const examples = useMemo(() => codeExamples(locale), [locale]);
  const [activeExample, setActiveExample] = useState(0);
  const [copied, setCopied] = useState(false);
  const active = examples[activeExample] ?? examples[0];

  const onCopy = async () => {
    if (!active) return;
    if (!navigator.clipboard) return;
    await navigator.clipboard.writeText(active.code);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  };

  return (
    <>
      <Head>
        <title>{zh ? '使用 WEEX API' : 'Build with WEEX APIs'}</title>
        <meta name="description" content={text.description} />
      </Head>
      <main className={styles.page}>
        <section className={styles.heroPanel}>
          <div className={styles.heroContent}>
            <h1>
              {text.title} <mark>WEEX API</mark>
            </h1>
            <p>{text.description}</p>
            <div className={styles.actions}>
              <Link className={styles.primary} to={`${prefix}/introduction`}>
                {text.primary}
              </Link>
              <Link className={styles.secondary} to={`${prefix}/catalog`}>
                {text.secondary}
              </Link>
            </div>
          </div>

          <div className={styles.codePanel} aria-label={zh ? '代码示例' : 'Code example'}>
            <div className={styles.codeHeader}>
              <div className={styles.tabs} role="tablist" aria-label={zh ? '代码语言' : 'Code languages'}>
                {examples.map((example, index) => (
                  <button
                    aria-selected={index === activeExample}
                    className={styles.tab}
                    key={example.label}
                    onClick={() => {
                      setActiveExample(index);
                      setCopied(false);
                    }}
                    role="tab"
                    type="button"
                  >
                    <span className={styles.tabIcon} aria-hidden="true">
                      <img src={example.iconSrc} alt="" loading="lazy" />
                    </span>
                    {example.label}
                  </button>
                ))}
              </div>
              <button className={styles.copyButton} onClick={onCopy} type="button" aria-label={copied ? (zh ? '已复制' : 'Copied') : (zh ? '复制代码' : 'Copy code')} title={copied ? (zh ? '已复制' : 'Copied') : (zh ? '复制代码' : 'Copy code')}>
                {copied ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
            <pre><code>{active?.code}</code></pre>
            <Link className={styles.connectorLink} to={`${prefix}/catalog`}>
              {text.connectorLink}<ArrowRight size={15} />
            </Link>
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.sectionIntro}>
            <span>{text.pathEyebrow}</span>
            <h2>{text.pathTitle}</h2>
            <p>{text.pathDescription}</p>
          </div>
          <div className={styles.pathGrid}>
            {pathCards(locale).map((card) => {
              const Icon = card.icon;
              return (
                <article className={styles.pathCard} key={card.title}>
                  <div className={styles.cardHead}>
                    <Icon size={20} />
                    <h3>{card.title}</h3>
                  </div>
                  <p>{card.description}</p>
                  <ul>
                    {card.links.map((link) => (
                      <li key={link.href}>
                        <Link to={link.href} reloadDocument={link.href.endsWith('.txt')}>{link.label}</Link>
                      </li>
                    ))}
                  </ul>
                </article>
              );
            })}
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.sectionIntro}>
            <span>{text.docsEyebrow}</span>
            <h2>{text.docsTitle}</h2>
            <p>{text.docsDescription}</p>
          </div>
          <div className={styles.linkGrid}>
            {featuredDocs(locale).map((doc) => {
              const Icon = doc.icon;
              return (
                <Link className={styles.linkCard} key={doc.href} to={doc.href}>
                  <Icon size={20} />
                  <strong>{doc.title}</strong>
                  <span>{doc.description}</span>
                </Link>
              );
            })}
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.sectionIntro}>
            <span>{text.communityEyebrow}</span>
            <h2>{text.communityTitle}</h2>
            <p>{text.communityDescription}</p>
          </div>
          <div className={styles.linkGrid}>
            {communityLinks(locale).map((link) => {
              const Icon = link.icon;
              const external = link.href.startsWith('http');
              const content = (
                <>
                  <Icon size={20} />
                  <strong>{link.title}</strong>
                  <span>{link.description}</span>
                </>
              );
              return external ? (
                <a className={styles.linkCard} href={link.href} key={link.href} rel="noreferrer" target="_blank">
                  {content}
                </a>
              ) : (
                <Link className={styles.linkCard} key={link.href} to={link.href}>
                  {content}
                </Link>
              );
            })}
          </div>
        </section>
      </main>
    </>
  );
}
